import {useState, type ChangeEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {APP_ICON_PLATFORMS, type AppIconPlatformId} from '../../../common/constants/appIconSizes'
import {trackEvent} from '../../../common/analytics/trackEvent'

interface IconPlatformSelectorProps {
  selected: ReadonlySet<AppIconPlatformId>
  onToggle: (id: AppIconPlatformId, enabled: boolean) => void
}

const IconPlatformSelector = ({selected, onToggle}: IconPlatformSelectorProps) => {
  const {t} = useTranslation()
  const [expanded, setExpanded] = useState<Set<AppIconPlatformId>>(new Set())

  const toggleExpand = (id: AppIconPlatformId) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="icg-platforms">
      {APP_ICON_PLATFORMS.map((platform) => {
        const isOn = selected.has(platform.id)
        const isOpen = expanded.has(platform.id)
        return (
          <div key={platform.id} className={`icg-platform${isOn ? ' is-on' : ''}`}>
            <label className="icg-platform-head">
              <input
                type="checkbox"
                checked={isOn}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  onToggle(platform.id, e.target.checked)
                  trackEvent('icon_platform_toggled', {platform: platform.id, enabled: e.target.checked})
                }}
              />
              <span className="icg-platform-name">{t(`iconGenerator.platform.${platform.id}.name`)}</span>
              <span className="icg-platform-count">
                {t('iconGenerator.platform.fileCount', {count: platform.sizes.length})}
              </span>
              <button
                type="button"
                className="icg-platform-expand"
                onClick={(e) => {
                  e.preventDefault()
                  toggleExpand(platform.id)
                }}
                aria-expanded={isOpen}
              >
                {isOpen ? '−' : '+'}
              </button>
            </label>
            {isOpen && (
              <ul className="icg-platform-sizes">
                {platform.sizes.map((size) => (
                  <li key={size.fileName}>
                    <span className="icg-size-px">
                      {size.pixels}×{size.pixels}
                    </span>
                    <span className="icg-size-name">{size.label}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default IconPlatformSelector
